import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  Image,
  Input,
  Button,
  Text,
  Container,
  HStack,
  Badge,
  useToast,
  Progress,
  Center,
  Spinner,
  Icon,
  Tooltip,
  useDisclosure,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from '@chakra-ui/react';
import { getRandomImage, resetUsedImages, resetRound } from '../services/imageService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaLightbulb, FaTrophy, FaArrowLeft, FaBolt, FaBullseye, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import { useGame } from '../context/GameContext';
import { saveGame, getStoredGames } from '../services/leaderboardService';
import LeaderboardModal from './LeaderboardModal';
import { MAX_GUESSES, TOTAL_ROUNDS, MAX_HINTS } from '../constants';
import GameCompletionModal from './GameCompletionModal';
import { motion, AnimatePresence } from 'framer-motion';
import { playSound, toggleMute } from '../services/soundService';
import { checkAchievements } from '../services/achievementService';
import AchievementPopup from './AchievementPopup';
import '../styles/animations.css';
import ComboIndicator from './ComboIndicator';
import AchievementsModal from './AchievementsModal';

const difficultyColors = {
  easy: "green",
  medium: "yellow",
  hard: "red"
};

const calculatePoints = (currentRound, difficulty = 'easy', currentCombo = 0) => {
  let basePoints = Math.max(150 - (currentRound * 30), 30);
  
  const multipliers = {
    'easy': 1,
    'medium': 1.5,
    'hard': 2
  };

  // Add combo bonus (10% per combo, up to 100% bonus)
  const comboMultiplier = Math.min(1 + (currentCombo * 0.1), 2);
  
  return Math.round(basePoints * (multipliers[difficulty] || 1) * comboMultiplier);
};

const GameScreen = () => {
  const [guess, setGuess] = useState('');
  const [gameRound, setGameRound] = useState(1);  // Current image (1-5)
  const [guessRound, setGuessRound] = useState(1);  // Current guess for this image (1-6)
  const [totalScore, setTotalScore] = useState(0);
  const [totalHintsUsed, setTotalHintsUsed] = useState(0);
  const [roundScores, setRoundScores] = useState([]); // Track scores for each round
  const [currentImage, setCurrentImage] = useState(null);
  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'lost'
  const [previousGuesses, setPreviousGuesses] = useState([]);
  const [hintsUsed, setHintsUsed] = useState(0);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categories = searchParams.get('categories');
  const { difficulty } = useGame();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [bestStreak, setBestStreak] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [categoryStats, setCategoryStats] = useState({});
  const [showCompletion, setShowCompletion] = useState(false);
  const [newAchievements, setNewAchievements] = useState([]);
  const [showAchievement, setShowAchievement] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const { 
    isOpen: isAchievementsOpen, 
    onOpen: onAchievementsOpen, 
    onClose: onAchievementsClose 
  } = useDisclosure();

  const startNewRound = useCallback(async () => {
    setCurrentImage(null);
    setPreviousGuesses([]);
    setHintsUsed(0);
    setGuessRound(1);
    resetRound(); // Reset used answers for new round
    
    try {
      const newImage = await getRandomImage(categories);
      if (newImage) {
        setCurrentImage(newImage);
        setGuess('');
        setGameState('playing');
      } else {
        toast({
          title: "Error",
          description: "Could not load image. Please try again.",
          status: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load image",
        status: "error",
        duration: 3000,
      });
    }
  }, [categories, toast]);

  useEffect(() => {
    // Reset game session when starting a new game
    resetUsedImages();
    startNewRound();
  }, [startNewRound]);

  const getHint = () => {
    if (hintsUsed >= MAX_HINTS) {
      toast({
        title: "No hints remaining",
        description: "You've used all available hints",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    playSound('hint');
    setHintsUsed(prev => prev + 1);

    // Updated hints array with more useful hints
    const availableHints = [
      `First letter: ${currentImage.answer[0].toUpperCase()}`,
      `Number of letters: ${currentImage.answer.length}`,
      `Last letter: ${currentImage.answer[currentImage.answer.length - 1].toUpperCase()}`
    ];

    toast({
      title: "Hint",
      description: availableHints[hintsUsed],
      status: "info",
      duration: 4000,
    });
  };

  const handleGameEnd = () => {
    playSound('gameOver');
    resetUsedImages();
    
    // Save game data
    const gameData = {
      score: totalScore,
      hintsUsed: totalHintsUsed,
      categories: categories ? categories.split(',') : ['all'],
      roundScores: roundScores,
      difficulty: difficulty.name,
      bestStreak,
      hardestCategory: Object.entries(categoryStats)
        .sort((a, b) => (a[1].attempts/a[1].correct) - (b[1].attempts/b[1].correct))[0]?.[0]
    };
    saveGame(gameData);

    setShowCompletion(true);
    setGameState('finished');
  };

  const handleGuess = () => {
    if (!guess.trim() || !currentImage) return;

    const normalizedGuess = guess.toLowerCase().trim();
    const isCorrect = 
      normalizedGuess === currentImage.answer.toLowerCase() ||
      currentImage.alternateAnswers.includes(normalizedGuess);

    setPreviousGuesses(prev => [...prev, {
      text: guess,
      correct: isCorrect
    }]);

    if (isCorrect) {
      playSound('correct');
      // Update streak first
      setCurrentStreak(prev => prev + 1);
      setBestStreak(prev => Math.max(prev, currentStreak + 1));
      
      // Update category stats
      setCategoryStats(prev => ({
        ...prev,
        [currentImage.category]: {
          attempts: (prev[currentImage.category]?.attempts || 0) + guessRound,
          correct: (prev[currentImage.category]?.correct || 0) + 1
        }
      }));

      setCombo(prev => prev + 1);
      setMaxCombo(prev => Math.max(prev, combo + 1));
      
      // Calculate points with difficulty
      const pointsEarned = calculatePoints(guessRound, currentImage.difficulty, combo);
      
      // Save round data
      const newRoundScore = { 
        round: gameRound, 
        score: pointsEarned, 
        hintsUsed,
        guessCount: guessRound,
        difficulty: currentImage.difficulty
      };
      setRoundScores(prev => [...prev, newRoundScore]);
      setTotalScore(prev => prev + pointsEarned);
      setTotalHintsUsed(prev => prev + hintsUsed);

      // Check for achievements
      const gameStats = {
        totalScore,
        roundScores: [...roundScores, newRoundScore],
        hintsUsed: totalHintsUsed,
        bestStreak,
        difficulty: difficulty.name,
        gamesPlayed: getStoredGames().length
      };

      const achievements = checkAchievements(gameStats);
      achievements.forEach(handleAchievement);

      if (gameRound < TOTAL_ROUNDS) {
        toast({
          title: "Correct!",
          description: `You earned ${pointsEarned} points! Next image coming up...`,
          status: "success",
          duration: 2000,
        });
        setGameRound(prev => prev + 1);
        setTimeout(startNewRound, 2000);
      } else {
        handleGameEnd();
      }
    } else {
      playSound('wrong');
      // Reset streak on wrong guess
      setCurrentStreak(0);
      setCombo(0);
      
      if (guessRound >= MAX_GUESSES) {
        setTotalHintsUsed(prev => prev + hintsUsed);
        const newRoundScore = { 
          round: gameRound, 
          score: 0, 
          hintsUsed,
          guessCount: MAX_GUESSES,
          difficulty: currentImage.difficulty
        };
        setRoundScores(prev => [...prev, newRoundScore]);

        if (gameRound < TOTAL_ROUNDS) {
          toast({
            title: "Out of guesses!",
            description: `The answer was "${currentImage.answer}". Next image coming up...`,
            status: "error",
            duration: 2000,
          });
          setGameRound(prev => prev + 1);
          setTimeout(startNewRound, 2000);
        } else {
          handleGameEnd();
        }
      } else {
        toast({
          title: "Try again!",
          description: "The image will become clearer",
          status: "info",
          duration: 2000,
        });
        setGuessRound(prev => prev + 1);
      }
    }
    setGuess('');
  };

  const getBlurAmount = () => {
    // Smoother blur reduction for 4 guesses
    const blurAmount = Math.max(
      difficulty.initialBlur * Math.pow(difficulty.blurStep, guessRound - 1),
      0
    );
    
    // Adjusted pixelation steps for 4 guesses
    const pixelSize = Math.max(12 - (guessRound * 2.5), 1);
    
    return {
      filter: `blur(${Math.round(blurAmount)}px)`,
      transform: `scale(${1/pixelSize}, ${1/pixelSize}) scale(${pixelSize}, ${pixelSize})`,
      imageRendering: 'pixelated'
    };
  };

  const getProgressValue = () => {
    return ((guessRound - 1) / (MAX_GUESSES - 1)) * 100;
  };

  const getSelectedCategoryNames = () => {
    if (!categories) return [];
    const categoryIds = categories.split(',');
    return categoryIds.map(id => ({
      id,
      name: categories[id]?.name || id,
      icon: categories[id]?.icon || '🎯'
    }));
  };

  const handleAchievement = (achievement) => {
    setNewAchievements(prev => [...prev, achievement]);
    setShowAchievement(true);
    playSound('achievement');
    setTimeout(() => setShowAchievement(false), 3000);
  };

  const handleToggleSound = () => {
    const newMutedState = toggleMute();
    setIsMuted(newMutedState);
  };

  if (!currentImage) return <Text>Loading...</Text>;

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} width="100%">
        {/* Header Section */}
        <Flex w="100%" justify="space-between" align="center">
          <Button
            variant="ghost"
            leftIcon={<Icon as={FaArrowLeft} />}
            onClick={() => navigate('/')}
          >
            Back
          </Button>
          <HStack>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleSound}
              leftIcon={<Icon as={isMuted ? FaVolumeMute : FaVolumeUp} />}
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </Button>
            <Badge colorScheme={difficulty.color} p={2} fontSize="md">
              {difficulty.name} Mode
            </Badge>
            {currentImage?.difficulty && (
              <Tooltip label={`${currentImage.difficulty} difficulty - ${
                currentImage.difficulty === 'easy' ? 'Normal points' :
                currentImage.difficulty === 'medium' ? '1.5x points' :
                '2x points'
              }`}>
                <Badge 
                  colorScheme={difficultyColors[currentImage.difficulty]} 
                  p={2} 
                  fontSize="md"
                >
                  {currentImage.difficulty.toUpperCase()}
                </Badge>
              </Tooltip>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onAchievementsOpen}
              leftIcon={<Icon as={FaTrophy} />}
            >
              Achievements
            </Button>
          </HStack>
          <Button
            colorScheme="purple"
            variant="outline"
            onClick={onOpen}
            leftIcon={<Icon as={FaTrophy} />}
          >
            Leaderboard
          </Button>
        </Flex>

        {/* Game Stats */}
        <SimpleGrid columns={4} spacing={4} w="100%">
          <Stat bg="blue.50" p={3} borderRadius="lg">
            <StatLabel>Round</StatLabel>
            <StatNumber>{gameRound}/{TOTAL_ROUNDS}</StatNumber>
            <StatHelpText>Current Progress</StatHelpText>
          </Stat>
          <Stat bg="green.50" p={3} borderRadius="lg">
            <StatLabel>Score</StatLabel>
            <StatNumber>{totalScore}</StatNumber>
            <StatHelpText>Total Points</StatHelpText>
          </Stat>
          <Stat bg="purple.50" p={3} borderRadius="lg">
            <StatLabel>Guesses</StatLabel>
            <StatNumber>{guessRound}/{MAX_GUESSES}</StatNumber>
            <StatHelpText>Remaining: {MAX_GUESSES - guessRound + 1}</StatHelpText>
          </Stat>
          <Stat bg="yellow.50" p={3} borderRadius="lg">
            <StatLabel>Hints</StatLabel>
            <StatNumber>{MAX_HINTS - hintsUsed}</StatNumber>
            <StatHelpText>Remaining</StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Categories */}
        {categories && (
          <HStack spacing={2} flexWrap="wrap" justify="center">
            {getSelectedCategoryNames().map(cat => (
              <Badge
                key={cat.id}
                colorScheme="blue"
                fontSize="sm"
                padding={2}
                borderRadius="full"
              >
                {cat.icon} {cat.name}
              </Badge>
            ))}
          </HStack>
        )}

        {/* Progress Bar */}
        <Progress 
          value={getProgressValue()} 
          width="100%" 
          colorScheme="blue" 
          height="8px" 
          borderRadius="full"
          hasStripe
          isAnimated
        />

        {/* Main Image Box */}
        <Box
          w="100%"
          maxW="600px"
          h="400px"
          position="relative"
          overflow="hidden"
          borderRadius="xl"
          boxShadow="2xl"
        >
          {currentImage ? (
            <>
              <motion.div
                animate={
                  previousGuesses.length > 0 
                    ? previousGuesses[previousGuesses.length - 1].correct 
                      ? "celebrate" 
                      : "shake"
                    : ""
                }
                variants={{
                  celebrate: {
                    scale: [1, 1.05, 1],
                    transition: { duration: 0.5 }
                  },
                  shake: {
                    x: [0, -10, 10, -10, 10, 0],
                    transition: { duration: 0.5 }
                  }
                }}
              >
                <Image
                  src={currentImage.url}
                  alt="Guess the image"
                  style={getBlurAmount()}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              </motion.div>
              <Text
                position="absolute"
                bottom="2"
                right="2"
                fontSize="xs"
                color="white"
                bg="blackAlpha.700"
                px="3"
                py="1"
                borderRadius="md"
              >
                Photo by{' '}
                <a href={currentImage.photographerUrl} target="_blank" rel="noopener noreferrer">
                  {currentImage.photographer}
                </a>
              </Text>
            </>
          ) : (
            <Center h="100%" bg="gray.100">
              <Spinner size="xl" />
            </Center>
          )}
        </Box>

        {/* Guess Input Section */}
        <Box w="100%" maxW="600px">
          <VStack spacing={4}>
            {gameState === 'playing' ? (
              <>
                <HStack width="100%">
                  <Input
                    placeholder="Enter your guess..."
                    value={guess}
                    onChange={(e) => setGuess(e.target.value)}
                    size="lg"
                    bg="white"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleGuess();
                    }}
                  />
                  <Tooltip label={`${MAX_HINTS - hintsUsed} hints remaining`}>
                    <Button
                      colorScheme="yellow"
                      size="lg"
                      onClick={getHint}
                      isDisabled={hintsUsed >= MAX_HINTS}
                    >
                      <Icon as={FaLightbulb} />
                    </Button>
                  </Tooltip>
                </HStack>
                <Button
                  colorScheme="blue"
                  width="100%"
                  onClick={handleGuess}
                  size="lg"
                  leftIcon={<Icon as={FaBullseye} />}
                >
                  Submit Guess
                </Button>
              </>
            ) : (
              <Button
                colorScheme={gameState === 'won' ? 'green' : 'red'}
                width="100%"
                onClick={startNewRound}
                size="lg"
                leftIcon={<Icon as={FaBolt} />}
              >
                {gameState === 'won' ? 'Play Again' : 'Try New Image'}
              </Button>
            )}

            {/* Previous Guesses */}
            {previousGuesses.length > 0 && (
              <Box width="100%" bg="gray.50" p={4} borderRadius="lg">
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Previous Guesses:
                </Text>
                <VStack spacing={2} align="stretch">
                  {previousGuesses.map((prevGuess, index) => (
                    <HStack
                      key={index}
                      p={2}
                      bg={prevGuess.correct ? "green.50" : "red.50"}
                      borderRadius="md"
                    >
                      <Text
                        color={prevGuess.correct ? "green.600" : "red.600"}
                        fontSize="sm"
                      >
                        {prevGuess.text}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>
      </VStack>

      <LeaderboardModal isOpen={isOpen} onClose={onClose} />
      <GameCompletionModal 
        isOpen={showCompletion}
        onClose={() => setShowCompletion(false)}
        stats={{
          totalScore,
          roundScores,
          hintsUsed: totalHintsUsed,
          bestStreak,
          maxCombo,
          correctGuesses: roundScores.filter(round => round.score > 0).length,
          averageGuesses: (roundScores.reduce((sum, round) => sum + round.guessCount, 0) / roundScores.length).toFixed(1)
        }}
        onPlayAgain={() => {
          setShowCompletion(false);
          window.location.reload();
        }}
        onShowLeaderboard={() => {
          setShowCompletion(false);
          onOpen();
        }}
      />

      <AnimatePresence>
        {showAchievement && newAchievements.length > 0 && (
          <AchievementPopup 
            achievement={newAchievements[newAchievements.length - 1]} 
          />
        )}
      </AnimatePresence>

      <ComboIndicator combo={combo} />

      <AchievementsModal isOpen={isAchievementsOpen} onClose={onAchievementsClose} />
    </Container>
  );
};

export default GameScreen; 